// src/components/leaves/LeaveTypesTable.js
import { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import LeaveTypeForm from './LeaveTypeForm';
import { leavesService } from '../../services/leavesService';

const LeaveTypesTable = ({ leaveTypes, loading, onRefresh, userType, userPremisesId, premises }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Separate common types (premises_id = 0) and organization-specific types
  const commonTypes = leaveTypes.filter(type => type.premises_id === 0 || type.premises_id === '0' || !type.premises_id);
  const orgTypes = leaveTypes.filter(type => type.premises_id && type.premises_id !== 0 && type.premises_id !== '0');

  const getTypeScope = (type) => {
    if (!type.premises_id || type.premises_id === 0 || type.premises_id === '0') {
      return { text: 'Common', class: 'scope-common', icon: 'fa-globe' };
    }
    return { text: 'Organization', class: 'scope-org', icon: 'fa-building' };
  };

  // Check if user can edit a leave type
  const canEdit = (type) => {
    // core_admin can edit all leave types
    if (userType === 'core_admin') {
      return true;
    }

    // client_admin can only edit leave types they created (premises_id matches any of their organization's premises)
    if (userType === 'client_admin' || userType === 'admin') {
      // Can't edit common leave types (premises_id = 0)
      if (!type.premises_id || type.premises_id === 0 || type.premises_id === '0') {
        return false;
      }

      // Can edit if premises_id matches any premises in their organization
      if (premises && premises.length > 0) {
        return premises.some(premise => premise.id == type.premises_id);
      }

      // Fallback: check against userPremisesId if premises array is not available
      return type.premises_id == userPremisesId;
    }

    return false;
  };

  const handleAddLeaveType = () => {
    setSelectedLeaveType(null);
    setShowAddModal(true);
  };

  const handleEditLeaveType = (type) => {
    console.log('Edit button clicked, leave type:', type);
    setSelectedLeaveType(type);
    setShowEditModal(true);
  };

  const handleDeleteLeaveType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) {
      return;
    }

    setSubmitting(true);
    try {
      await leavesService.deleteLeaveType(typeId);
      alert('Leave type deleted successfully!');
      onRefresh();
    } catch (error) {
      alert(error.message || 'Failed to delete leave type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    setSubmitting(true);
    try {
      if (selectedLeaveType && selectedLeaveType.id) {
        // Edit existing leave type
        console.log('Updating leave type with ID:', selectedLeaveType.id, 'Data:', formData);
        // Include the ID in the formData for the API
        const updateData = {
          ...formData,
          id: selectedLeaveType.id
        };
        await leavesService.updateLeaveType(selectedLeaveType.id, updateData);
        alert('Leave type updated successfully!');
      } else {
        // Create new leave type
        console.log('Creating new leave type with data:', formData);
        await leavesService.createLeaveType(formData);
        alert('Leave type created successfully!');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedLeaveType(null);
      onRefresh();
    } catch (error) {
      console.error('Error in handleFormSubmit:', error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedLeaveType(null);
  };

  if (loading) {
    return (
      <div className="leave-types-table-container">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="leave-types-table-container">
      {/* Header */}
      <div className="types-header">
        <div className="header-info">
          <h3>
            <i className="fas fa-tags"></i>
            Leave Types Configuration
          </h3>
          <p>Manage common and organization-specific leave types</p>
        </div>
        {userType !== 'app_admin' && (
          <button className="btn btn-primary btn-gradient" onClick={handleAddLeaveType}>
            <i className="fas fa-plus-circle"></i>
            Add Leave Type
          </button>
        )}
      </div>

      {/* Common Leave Types Section */}
      {commonTypes.length > 0 && (
        <div className="types-section">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-globe"></i>
              <h4>Common Leave Types</h4>
              <span className="section-badge">{commonTypes.length}</span>
            </div>
            <p className="section-description">Available to all organizations</p>
          </div>

          <div className="types-grid">
            {commonTypes.map(type => {
              const scope = getTypeScope(type);
              return (
                <div key={type.id} className="type-card">
                  <div className="card-header">
                    <div className="type-name">
                      <i className="fas fa-calendar-alt"></i>
                      <h5>{type.leave_name}</h5>
                    </div>
                    <span className={`scope-badge ${scope.class}`}>
                      <i className={`fas ${scope.icon}`}></i>
                      {scope.text}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="type-details">
                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-calendar-day"></i>
                          Max Days
                        </span>
                        <span className="detail-value">
                          {type.max_days || 'Unlimited'}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-redo"></i>
                          Carry Forward
                        </span>
                        <span className={`detail-badge ${type.carry_forward ? 'badge-yes' : 'badge-no'}`}>
                          {type.carry_forward ? 'Yes' : 'No'}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-check-circle"></i>
                          Requires Approval
                        </span>
                        <span className={`detail-badge ${type.requires_approval ? 'badge-yes' : 'badge-no'}`}>
                          {type.requires_approval ? 'Yes' : 'No'}
                        </span>
                      </div>

                      {type.description && (
                        <div className="detail-row full-width">
                          <span className="detail-label">
                            <i className="fas fa-info-circle"></i>
                            Description
                          </span>
                          <span className="detail-description">{type.description}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {canEdit(type) && (
                    <div className="card-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEditLeaveType(type)}
                        title="Edit Leave Type"
                        disabled={submitting}
                      >
                        <i className="fas fa-edit"></i>
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Organization-Specific Leave Types Section */}
      {orgTypes.length > 0 && (
        <div className="types-section">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-building"></i>
              <h4>Organization-Specific Leave Types</h4>
              <span className="section-badge">{orgTypes.length}</span>
            </div>
            <p className="section-description">Custom leave types for specific organizations</p>
          </div>

          <div className="types-grid">
            {orgTypes.map(type => {
              const scope = getTypeScope(type);
              return (
                <div key={type.id} className="type-card">
                  <div className="card-header">
                    <div className="type-name">
                      <i className="fas fa-calendar-alt"></i>
                      <h5>{type.leave_name}</h5>
                    </div>
                    <span className={`scope-badge ${scope.class}`}>
                      <i className={`fas ${scope.icon}`}></i>
                      {scope.text}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="type-details">
                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-calendar-day"></i>
                          Max Days
                        </span>
                        <span className="detail-value">
                          {type.max_days || 'Unlimited'}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-redo"></i>
                          Carry Forward
                        </span>
                        <span className={`detail-badge ${type.carry_forward ? 'badge-yes' : 'badge-no'}`}>
                          {type.carry_forward ? 'Yes' : 'No'}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">
                          <i className="fas fa-check-circle"></i>
                          Requires Approval
                        </span>
                        <span className={`detail-badge ${type.requires_approval ? 'badge-yes' : 'badge-no'}`}>
                          {type.requires_approval ? 'Yes' : 'No'}
                        </span>
                      </div>

                      {type.description && (
                        <div className="detail-row full-width">
                          <span className="detail-label">
                            <i className="fas fa-info-circle"></i>
                            Description
                          </span>
                          <span className="detail-description">{type.description}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {canEdit(type) && (
                    <div className="card-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEditLeaveType(type)}
                        title="Edit Leave Type"
                        disabled={submitting}
                      >
                        <i className="fas fa-edit"></i>
                        Edit
                      </button>
                      {/*
                      <button
                        className="action-btn delete"
                        onClick={() => handleDeleteLeaveType(type.id)}
                        title="Delete Leave Type"
                        disabled={submitting}
                      >
                        <i className="fas fa-trash-alt"></i>
                        Delete
                      </button>*/}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {leaveTypes.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-calendar-times"></i>
          </div>
          <h3>No Leave Types</h3>
          <p>Create leave types to start managing staff leave requests</p>
          {userType !== 'app_admin' && (
            <button className="btn btn-primary btn-gradient" onClick={handleAddLeaveType}>
              <i className="fas fa-plus-circle"></i>
              Create First Leave Type
            </button>
          )}
        </div>
      )}

      {/* Add Leave Type Modal */}
      <Modal
        show={showAddModal}
        onClose={handleCloseModals}
        title="Add Leave Type"
        size="medium"
      >
        <LeaveTypeForm
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModals}
          userType={userType}
          userPremisesId={userPremisesId}
          premises={premises}
          submitting={submitting}
        />
      </Modal>

      {/* Edit Leave Type Modal */}
      <Modal
        show={showEditModal}
        onClose={handleCloseModals}
        title="Edit Leave Type"
        size="medium"
      >
        <LeaveTypeForm
          leaveType={selectedLeaveType}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModals}
          userType={userType}
          userPremisesId={userPremisesId}
          premises={premises}
          submitting={submitting}
        />
      </Modal>

      <style jsx>{`
        .leave-types-table-container {
          background: white;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        .types-header {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          padding: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-info h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .header-info p {
          margin: 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.95rem;
        }

        .btn-gradient {
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.5);
          color: white;
          padding: 0.8rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-gradient:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: white;
          transform: translateY(-2px);
        }

        .types-section {
          padding: 2rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .types-section:last-child {
          border-bottom: none;
        }

        .section-header {
          margin-bottom: 1.5rem;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.5rem;
        }

        .section-title i {
          color: #f59e0b;
          font-size: 1.3rem;
        }

        .section-title h4 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
        }

        .section-badge {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 700;
        }

        .section-description {
          color: #718096;
          font-size: 0.9rem;
          margin: 0;
          padding-left: 2.1rem;
        }

        .types-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .type-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 15px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .type-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          border-color: #f59e0b;
        }

        .card-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 1.2rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #e2e8f0;
        }

        .type-name {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          flex: 1;
        }

        .type-name i {
          color: #f59e0b;
          font-size: 1.3rem;
        }

        .type-name h5 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #2d3748;
        }

        .scope-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .scope-badge.scope-common {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .scope-badge.scope-org {
          background: rgba(139, 92, 246, 0.15);
          color: #8b5cf6;
        }

        .card-body {
          padding: 1.2rem;
        }

        .type-details {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.6rem 0;
          border-bottom: 1px solid #f1f3f5;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row.full-width {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .detail-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #718096;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .detail-label i {
          color: #f59e0b;
          font-size: 0.8rem;
        }

        .detail-value {
          color: #2d3748;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .detail-badge {
          padding: 0.3rem 0.7rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .detail-badge.badge-yes {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .detail-badge.badge-no {
          background: rgba(107, 114, 128, 0.15);
          color: #6b7280;
        }

        .detail-description {
          color: #2d3748;
          font-size: 0.85rem;
          line-height: 1.5;
          padding: 0.5rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .card-actions {
          padding: 1rem 1.2rem;
          background: #f8f9fa;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 0.8rem;
        }

        .action-btn {
          flex: 1;
          padding: 0.7rem 1rem;
          border: none;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .action-btn.edit {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
          border: 2px solid rgba(245, 158, 11, 0.3);
        }

        .action-btn.edit:hover {
          background: rgba(245, 158, 11, 0.2);
          border-color: #f59e0b;
          transform: translateY(-2px);
        }

        .action-btn.delete {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 2px solid rgba(239, 68, 68, 0.3);
        }

        .action-btn.delete:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: #ef4444;
          transform: translateY(-2px);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .empty-icon i {
          font-size: 3rem;
          color: #f59e0b;
        }

        .empty-state h3 {
          color: #2d3748;
          margin-bottom: 0.8rem;
          font-size: 1.5rem;
        }

        .empty-state p {
          color: #718096;
          max-width: 500px;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .types-header {
            flex-direction: column;
            gap: 1.5rem;
            text-align: center;
          }

          .types-grid {
            grid-template-columns: 1fr;
          }

          .card-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default LeaveTypesTable;
